import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import {
  Add,
  Delete,
  Edit,
  FilterList,
  Search,
  ViewList,
  ViewModule,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { logger } from '../../../utils/logger';
import { Permission, Role, Tab as TabType, User } from '../../../types';
import { CorporateBadge, CorporateDialog } from '../../../components/ui';

export const RolesManagement: React.FC = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const [roles, setRoles] = useState<Role[]>([]);
  const [tabs, setTabs] = useState<TabType[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roleTabs, setRoleTabs] = useState<TabType[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(false);
  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', description: '' });
  const [viewMode, setViewMode] = useState<'detailed' | 'matrix'>('detailed');
  const [filterRole, setFilterRole] = useState('');
  const [filterPermission, setFilterPermission] = useState('');
  const [filterTab, setFilterTab] = useState('');
  const [showSystemRoles, setShowSystemRoles] = useState(true);
  const [openEditRoleDialog, setOpenEditRoleDialog] = useState(false);
  const [openDeleteRoleDialog, setOpenDeleteRoleDialog] = useState(false);
  const [roleToEdit, setRoleToEdit] = useState<Role | null>(null);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [roleUsers, setRoleUsers] = useState<User[]>([]);
  const [newRoleForUsers, setNewRoleForUsers] = useState('');
  const [loadingRoleUsers, setLoadingRoleUsers] = useState(false);
  const [editRoleData, setEditRoleData] = useState({ name: '', description: '' });

  useEffect(() => {
    loadRoles();
    loadTabs();
    loadPermissions();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      loadRoleTabs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);

  useEffect(() => {
    if (!selectedRole) return;
    const exists = roles.find((role) => role.id === selectedRole.id);
    if (!exists) {
      setSelectedRole(null);
    }
  }, [roles, selectedRole]);

  const filteredRoles = useMemo(
    () =>
      roles.filter((role) => {
        if (!showSystemRoles && role.is_system) return false;
        if (filterRole) {
          return (
            role.name.toLowerCase().includes(filterRole.toLowerCase()) ||
            (role.description || '').toLowerCase().includes(filterRole.toLowerCase())
          );
        }
        return true;
      }),
    [roles, showSystemRoles, filterRole],
  );

  const filteredPermissions = useMemo(
    () =>
      permissions.filter((perm) => {
        if (filterPermission) {
          return (
            perm.name.toLowerCase().includes(filterPermission.toLowerCase()) ||
            (perm.description || '').toLowerCase().includes(filterPermission.toLowerCase())
          );
        }
        return true;
      }),
    [permissions, filterPermission],
  );

  const filteredTabs = useMemo(
    () =>
      tabs.filter((tab) => {
        if (filterTab) {
          return (
            tab.label.toLowerCase().includes(filterTab.toLowerCase()) ||
            tab.name.toLowerCase().includes(filterTab.toLowerCase()) ||
            (tab.description || '').toLowerCase().includes(filterTab.toLowerCase())
          );
        }
        return true;
      }),
    [tabs, filterTab],
  );

  const loadPermissions = async () => {
    try {
      const permissionsData = await apiService.getPermissions();
      setPermissions(permissionsData);
    } catch (error: any) {
      logger.error('Error cargando permisos:', error);
    }
  };

  const loadRoles = async () => {
    try {
      const rolesData = await apiService.getRoles();
      setRoles(rolesData);
    } catch (error: any) {
      showError(error.response?.data?.detail || t('admin.loadingError'));
    }
  };

  const loadTabs = async () => {
    try {
      const tabsData = await apiService.getAllTabs();
      setTabs(tabsData);
    } catch (error: any) {
      showError(error.response?.data?.detail || t('admin.loadingError'));
    }
  };

  const loadRoleTabs = async () => {
    if (!selectedRole) return;
    try {
      setLoading(true);
      const tabsData = await apiService.getRoleTabs(selectedRole.id);
      setRoleTabs(tabsData);
    } catch (error: any) {
      logger.error('Error cargando tabs del rol:', error);
      showError(error.response?.data?.detail || t('admin.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (role: Role) => {
    setSelectedRole(role);
  };

  const handleTabToggle = async (tabId: string, checked: boolean, roleId?: string) => {
    const targetRoleId = roleId || selectedRole?.id;
    if (!targetRoleId) return;

    try {
      const roleTabsData = await apiService.getRoleTabs(targetRoleId);
      const currentTabIds = roleTabsData.filter((tab) => tab.role_ids.includes(targetRoleId)).map((tab) => tab.id);

      const newTabIds = checked ? [...currentTabIds, tabId] : currentTabIds.filter((id) => id !== tabId);

      await apiService.updateRoleTabs(targetRoleId, newTabIds);
      showSuccess(t('admin.tabPermissionsUpdated'));
      if (selectedRole?.id === targetRoleId) {
        loadRoleTabs();
      }
      loadTabs();
      loadRoles();
    } catch (error: any) {
      showError(error.response?.data?.detail || t('admin.loadingError'));
    }
  };

  const handlePermissionToggle = async (permissionId: string, checked: boolean, roleId: string) => {
    const role = roles.find((r) => r.id === roleId);
    if (!role) return;

    const currentPermissionIds = role.permission_ids || [];
    const newPermissionIds = checked
      ? [...currentPermissionIds, permissionId]
      : currentPermissionIds.filter((id) => id !== permissionId);

    try {
      await apiService.updateRole(roleId, {
        permission_ids: newPermissionIds,
      });
      showSuccess(t('admin.permissionsUpdated'));
      loadRoles();
    } catch (error: any) {
      showError(error.response?.data?.detail || t('admin.loadingError'));
    }
  };

  const handleCreateRole = async () => {
    if (!newRole.name) {
      showError(t('admin.roleNameRequired'));
      return;
    }
    try {
      await apiService.createRole({
        name: newRole.name,
        description: newRole.description,
        permission_ids: [],
      });
      showSuccess(t('admin.roleCreated'));
      setOpenRoleDialog(false);
      setNewRole({ name: '', description: '' });
      loadRoles();
    } catch (error: any) {
      showError(error.response?.data?.detail || t('admin.loadingError'));
    }
  };

  const handleEditRole = (role: Role, event: React.MouseEvent) => {
    event.stopPropagation();
    if (role.is_system) {
      showError(t('admin.cannotEditSystemRole'));
      return;
    }
    setRoleToEdit(role);
    setEditRoleData({
      name: role.name,
      description: role.description || '',
    });
    setOpenEditRoleDialog(true);
  };

  const handleSaveEditRole = async () => {
    if (!roleToEdit || !editRoleData.name) {
      showError(t('admin.roleNameRequired'));
      return;
    }
    try {
      await apiService.updateRole(roleToEdit.id, {
        name: editRoleData.name,
        description: editRoleData.description,
      });
      showSuccess(t('admin.roleUpdated'));
      setOpenEditRoleDialog(false);
      setRoleToEdit(null);
      setEditRoleData({ name: '', description: '' });
      loadRoles();
      if (selectedRole?.id === roleToEdit.id) {
        setSelectedRole(null);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || t('admin.roleEditError');
      if (errorMessage.includes('ya existe') || errorMessage.includes('already exists')) {
        showError(t('admin.roleNameExists'));
      } else {
        showError(errorMessage);
      }
    }
  };

  const handleDeleteRole = async (role: Role, event: React.MouseEvent) => {
    event.stopPropagation();
    if (role.is_system) {
      showError(t('admin.cannotDeleteSystemRole'));
      return;
    }
    setRoleToDelete(role);
    setNewRoleForUsers('');
    setLoadingRoleUsers(true);
    try {
      const users = await apiService.getRoleUsers(role.id);
      setRoleUsers(users);
    } catch (error: any) {
      showError(error.response?.data?.detail || t('admin.loadingError'));
      setLoadingRoleUsers(false);
      return;
    } finally {
      setLoadingRoleUsers(false);
    }
    setOpenDeleteRoleDialog(true);
  };

  const handleConfirmDeleteRole = async () => {
    if (!roleToDelete) return;

    if (!newRoleForUsers || roleToDelete.id === newRoleForUsers) {
      showError(t('admin.selectNewRoleRequired'));
      return;
    }

    try {
      const result = await apiService.deleteRole(roleToDelete.id, newRoleForUsers);
      showSuccess(result.message || t('admin.roleDeleted'));
      setOpenDeleteRoleDialog(false);
      setRoleToDelete(null);
      setRoleUsers([]);
      setNewRoleForUsers('');
      loadRoles();
      if (selectedRole?.id === roleToDelete.id) {
        setSelectedRole(null);
      }
    } catch (error: any) {
      showError(error.response?.data?.detail || t('admin.roleDeleteError'));
    }
  };

  const hasTabPermission = (roleId: string, tabId: string) => {
    const tab = tabs.find((t) => t.id === tabId);
    return tab?.role_ids.includes(roleId) || false;
  };

  const hasPermission = (roleId: string, permissionId: string) => {
    const role = roles.find((r) => r.id === roleId);
    return role?.permission_ids.includes(permissionId) || false;
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        sx={{
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            letterSpacing: '-0.5px',
          }}
        >
          {t('admin.roleManagement')}
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <Button
            variant={viewMode === 'detailed' ? 'contained' : 'outlined'}
            startIcon={<ViewList />}
            onClick={() => setViewMode('detailed')}
            size="small"
          >
            {t('admin.detailedView')}
          </Button>
          <Button
            variant={viewMode === 'matrix' ? 'contained' : 'outlined'}
            startIcon={<ViewModule />}
            onClick={() => setViewMode('matrix')}
            size="small"
          >
            {t('admin.matrixView')}
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => setOpenRoleDialog(true)} sx={{ ml: 1 }}>
            {t('admin.newRole')}
          </Button>
        </Box>
      </Box>

      {viewMode === 'detailed' ? (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                height: 'fit-content',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <FilterList color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('admin.roles')}
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder={t('admin.filterRoles')}
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Checkbox checked={showSystemRoles} onChange={(e) => setShowSystemRoles(e.target.checked)} />
                }
                label={t('admin.showSystemRoles')}
                sx={{ mb: 2 }}
              />
              {filteredRoles.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('admin.noRoles')}
                </Alert>
              ) : (
                filteredRoles.map((role) => (
                  <Box
                    key={role.id}
                    sx={{
                      p: 2,
                      mb: 1.5,
                      cursor: 'pointer',
                      borderRadius: 2,
                      backgroundColor: selectedRole?.id === role.id ? 'primary.light' : 'background.paper',
                      border: selectedRole?.id === role.id ? '2px solid' : '1px solid',
                      borderColor: selectedRole?.id === role.id ? 'primary.main' : 'divider',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: selectedRole?.id === role.id ? 'primary.light' : 'action.hover',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                      },
                    }}
                    onClick={() => handleRoleSelect(role)}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {role.name}
                      </Typography>
                      <Box display="flex" gap={1} alignItems="center">
                        <CorporateBadge
                          variant="system"
                          type={role.is_system ? 'system-yes' : 'system-no'}
                          label={role.is_system ? t('admin.systemRole') : t('admin.customRole')}
                          showIcon
                          gradient={false}
                          shadow
                        />
                        {!role.is_system && (
                          <>
                            <Tooltip title={t('admin.editRole')} arrow>
                              <IconButton size="small" onClick={(e) => handleEditRole(role, e)} color="primary" sx={{ ml: 0.5 }}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('admin.deleteRole')} arrow>
                              <IconButton size="small" onClick={(e) => handleDeleteRole(role, e)} color="error">
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </Box>
                    {role.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {role.description}
                      </Typography>
                    )}
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <CorporateBadge
                        variant="status"
                        type={role.is_active ? 'status-active' : 'status-inactive'}
                        label={role.is_active ? t('admin.active') : t('admin.inactive')}
                        showIcon
                        gradient
                        shadow
                      />
                      {role.permission_names && role.permission_names.length > 0 && (
                        <CorporateBadge
                          variant="custom"
                          type="custom-info"
                          label={`${role.permission_names.length} ${t('admin.permissions')}`}
                          showIcon={false}
                          gradient={false}
                          shadow
                        />
                      )}
                    </Box>
                  </Box>
                ))
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            {selectedRole ? (
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('admin.tabPermissions')} - {selectedRole.name}
                  </Typography>
                </Box>
                {loading ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box>
                    {filteredTabs.map((tab) => {
                      const hasAccess = roleTabs.some((rt) => rt.id === tab.id && rt.role_ids.includes(selectedRole.id));
                      return (
                        <Paper
                          key={tab.id}
                          sx={{
                            p: 2,
                            mb: 2,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: hasAccess ? 'success.main' : 'divider',
                            backgroundColor: hasAccess ? 'success.light' : 'background.paper',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            },
                          }}
                        >
                          <FormControlLabel
                            control={<Checkbox checked={hasAccess} onChange={(e) => handleTabToggle(tab.id, e.target.checked)} color="success" />}
                            label={
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {tab.label}
                                </Typography>
                                {tab.description && (
                                  <Typography variant="body2" color="text.secondary">
                                    {tab.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </Paper>
                      );
                    })}
                    {filteredTabs.length === 0 && <Alert severity="info">{t('admin.noTabsAvailable')}</Alert>}
                  </Box>
                )}
              </Paper>
            ) : (
              <Paper
                sx={{
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {t('admin.selectRoleToManageTabs')}
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      ) : (
        <Box>
          <Paper
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t('admin.filterRoles')}
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t('admin.filterTabs')}
                  value={filterTab}
                  onChange={(e) => setFilterTab(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Checkbox checked={showSystemRoles} onChange={(e) => setShowSystemRoles(e.target.checked)} />
                  }
                  label={t('admin.showSystemRoles')}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              {t('admin.allTabsPermissions')}
            </Typography>
            <TableContainer sx={{ maxHeight: 480, overflowX: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>{t('admin.role')}</TableCell>
                    {filteredTabs.map((tab) => (
                      <TableCell key={tab.id} sx={{ fontWeight: 600, textAlign: 'center', minWidth: 120 }}>
                        <Tooltip title={tab.description || ''} arrow>
                          <Typography variant="body2" noWrap>
                            {tab.label}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRoles.map((role) => (
                    <TableRow key={role.id} hover>
                      <TableCell>
                        <Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {role.name}
                            </Typography>
                            {!role.is_system && (
                              <>
                                <Tooltip title={t('admin.editRole')} arrow>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditRole(role, e);
                                    }}
                                    color="primary"
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t('admin.deleteRole')} arrow>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteRole(role, e);
                                    }}
                                    color="error"
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                          <Box display="flex" gap={0.5} mt={0.5}>
                            <CorporateBadge
                              variant="system"
                              type={role.is_system ? 'system-yes' : 'system-no'}
                              label={role.is_system ? t('admin.systemRole') : t('admin.customRole')}
                              showIcon
                              gradient={false}
                              shadow
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      {filteredTabs.map((tab) => {
                        const hasAccess = hasTabPermission(role.id, tab.id);
                        return (
                          <TableCell key={tab.id} sx={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={hasAccess}
                              onChange={(e) => handleTabToggle(tab.id, e.target.checked, role.id)}
                              color="primary"
                              size="small"
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {permissions.length > 0 && (
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('admin.allRolesPermissions')}
                </Typography>
                <TextField
                  size="small"
                  placeholder={t('admin.filterPermissions')}
                  value={filterPermission}
                  onChange={(e) => setFilterPermission(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                  sx={{ width: 200 }}
                />
              </Box>
              <TableContainer sx={{ maxHeight: 480, overflowX: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, minWidth: 160 }}>{t('admin.role')}</TableCell>
                      {filteredPermissions.map((perm) => (
                        <TableCell key={perm.id} sx={{ fontWeight: 600, textAlign: 'center', minWidth: 120 }}>
                          <Tooltip title={perm.description || ''} arrow>
                            <Typography variant="body2" noWrap>
                              {perm.name}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRoles.map((role) => (
                      <TableRow key={role.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {role.name}
                            </Typography>
                            <Box display="flex" gap={0.5} mt={0.5}>
                              <Chip
                                label={role.is_system ? t('admin.systemRole') : t('admin.customRole')}
                                size="small"
                                color={role.is_system ? 'default' : 'primary'}
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        </TableCell>
                        {filteredPermissions.map((perm) => {
                          const hasAccess = hasPermission(role.id, perm.id);
                          return (
                            <TableCell key={perm.id} sx={{ textAlign: 'center' }}>
                              <Checkbox
                                checked={hasAccess}
                                onChange={(e) => handlePermissionToggle(perm.id, e.target.checked, role.id)}
                                color="primary"
                                size="small"
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      )}

      <CorporateDialog
        open={openRoleDialog}
        onClose={() => setOpenRoleDialog(false)}
        title={t('admin.createNewRole')}
        icon={<Add />}
        maxWidth="sm"
        fullWidth
        actions={
          <>
            <Button onClick={() => setOpenRoleDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreateRole} variant="contained">
              {t('common.save')}
            </Button>
          </>
        }
      >
        <TextField
          fullWidth
          label={t('admin.roleName')}
          value={newRole.name}
          onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
          sx={{ mb: 2 }}
          required
        />
        <TextField
          fullWidth
          label={t('admin.roleDescription')}
          multiline
          rows={3}
          value={newRole.description}
          onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
        />
      </CorporateDialog>

      <CorporateDialog
        open={openEditRoleDialog}
        onClose={() => setOpenEditRoleDialog(false)}
        title={t('admin.editRole')}
        icon={<Edit />}
        maxWidth="sm"
        fullWidth
        actions={
          <>
            <Button onClick={() => setOpenEditRoleDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSaveEditRole} variant="contained">
              {t('common.save')}
            </Button>
          </>
        }
      >
        <TextField
          fullWidth
          label={t('admin.roleName')}
          value={editRoleData.name}
          onChange={(e) => setEditRoleData({ ...editRoleData, name: e.target.value })}
          sx={{ mb: 2 }}
          required
        />
        <TextField
          fullWidth
          label={t('admin.roleDescription')}
          multiline
          rows={3}
          value={editRoleData.description}
          onChange={(e) => setEditRoleData({ ...editRoleData, description: e.target.value })}
        />
      </CorporateDialog>

      <CorporateDialog
        open={openDeleteRoleDialog}
        onClose={() => setOpenDeleteRoleDialog(false)}
        title={t('admin.deleteRoleConfirm')}
        icon={<Delete />}
        maxWidth="md"
        fullWidth
        actions={
          <>
            <Button onClick={() => setOpenDeleteRoleDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleConfirmDeleteRole} variant="contained" color="error" disabled={!newRoleForUsers || loadingRoleUsers}>
              {t('admin.deleteRole')}
            </Button>
          </>
        }
      >
        {roleToDelete && (
          <>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                {t('admin.deleteRoleWarning')}
              </Typography>
              <Typography variant="body2">{t('admin.deleteRoleInfo')}</Typography>
            </Alert>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t('admin.role')}: {roleToDelete.name}
              </Typography>
              {roleToDelete.description && (
                <Typography variant="body2" color="text.secondary">
                  {roleToDelete.description}
                </Typography>
              )}
            </Box>

            {loadingRoleUsers ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('admin.usersWithRole')} ({roleUsers.length})
                </Typography>

                {roleUsers.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 160 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {roleUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.display_name}</TableCell>
                            <TableCell>
                              <CorporateBadge
                                variant="status"
                                type={user.is_active ? 'status-active' : 'status-inactive'}
                                label={user.is_active ? t('admin.active') : t('admin.inactive')}
                                showIcon
                                gradient
                                shadow
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    {t('admin.noUsersWithRole')}
                  </Alert>
                )}

                <FormControl fullWidth required>
                  <InputLabel>{t('admin.selectNewRole')}</InputLabel>
                  <Select
                    value={newRoleForUsers}
                    onChange={(e) => setNewRoleForUsers(e.target.value)}
                    label={t('admin.selectNewRole')}
                  >
                    {roles
                      .filter((r) => r.id !== roleToDelete.id && r.is_active)
                      .map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.name}
                          {role.is_system && (
                            <CorporateBadge
                              variant="system"
                              type="system-yes"
                              label={t('admin.systemRole')}
                              showIcon
                              gradient={false}
                              shadow
                              sx={{ ml: 1 }}
                            />
                          )}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </>
            )}
          </>
        )}
      </CorporateDialog>
    </Box>
  );
};

